import classes from "./clientOnboarding.module.css";
import styles from "./clientOnboarding.module.css";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "../../assets/images/Onboarding/logo.svg";
import FullScreenLoader from "../../components/Loaders/FullScreen/loader";
import { useState, useEffect, useContext, useRef } from "react";
import { errorHandler } from "../../utils";
import { companyProfile } from "../../services/clientCalls";
import { UserContext } from "../../context";
import { IconPlus } from "@tabler/icons-react";
import StepBar from "./StepBar";

function smoothScrollTop(duration = 250) {
  return new Promise((resolve) => {
    const startY = window.scrollY;
    if (startY < 10) {
      resolve();
      return;
    }
    const startT = performance.now();
    function step(ts) {
      const elapsed = ts - startT;
      const progress = Math.min(elapsed / duration, 1);
      const ease =
        progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
      window.scrollTo(0, startY * (1 - ease));
      if (elapsed < duration) requestAnimationFrame(step);
      else {
        window.scrollTo(0, 0);
        resolve();
      }
    }
    requestAnimationFrame(step);
  });
}

const OnboardingLayout = ({ children }) => {
  const { setData } = useContext(UserContext);
  const [load, setLoad] = useState(false);
  const [direction, setDirection] = useState("forward"); // 'forward' | 'backward'
  const [isAnimating, setIsAnimating] = useState(false);
  const [exitingContent, setExitingContent] = useState(null);
  const ANIM_DURATION = 700;
  const [searchParams] = useSearchParams();

  // Derive step from URL — clamped to 1–5, defaults to 1
  const raw = parseInt(searchParams.get("stage") || "1", 10);
  const currentStep = isNaN(raw) ? 1 : Math.min(Math.max(raw, 1), 5);
  const showComplete = currentStep === 5;
  const prevStepRef = useRef(currentStep);
  const animTimerRef = useRef(null);

  const prevChildrenRef = useRef(children);
  const prevChildren = prevChildrenRef.current;

  useEffect(() => {
    prevChildrenRef.current = children;
  });

  useEffect(() => {
    const prevStep = prevStepRef.current;
    if (prevStep === currentStep) return;

    const forward = currentStep > prevStep;
    setDirection(forward ? "forward" : "backward");

    // prevChildren is captured from render phase via closure → holds old panel
    setExitingContent(prevChildren);
    setIsAnimating(true);

    prevStepRef.current = currentStep;

    clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setExitingContent(null);
      setIsAnimating(false);
    }, ANIM_DURATION);

    smoothScrollTop(250);
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps
  // prevChildren intentionally omitted — we want the closure value, not latest

  // ── Cleanup timer on unmount ─────
  useEffect(() => {
    if (window.history.scrollRestoration)
      window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    return () => clearTimeout(animTimerRef.current);
  }, []);

  const getProfile = async () => {
    try {
      const res = await companyProfile();

      setData(res.data.data.client);
      setLoad(false);
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  return (
    <>
      {/* {!!load && <FullScreenLoader />} */}
      {!load && (
        <div className={`onboarding-pageWrapper`}>
          <header className={styles.topbar}>
            <Link to="/" className={styles.topbarLogo}>
              <img src={Logo} alt="Awasource Logo" />
            </Link>
            <div className={styles.topbarRight}>
              <Link to="/add-new-job/job" className={styles.topbarLink}>
                <IconPlus />
                Add Gig/Job
              </Link>
            </div>
          </header>
            <StepBar currentStep={currentStep} completed={false} />
          <>
            <div className={styles.obBody}>
              <div className={styles.obShell}>
                {/* Steps — carousel with enter/exit animation */}
                {!showComplete && (
                  <div
                    className={`${styles.obCard} ${isAnimating ? styles.obCardTransitioning : ""}`}
                    id="stepsCard"
                  >
                    {/* Exiting panel — holds the old children, animated off-screen */}
                    {exitingContent && (
                      <div
                        className={[
                          styles.obPanel,
                          styles.obPanelExiting,
                          direction === "forward"
                            ? styles.animExitLeft
                            : styles.animExitRight,
                        ].join(" ")}
                      >
                        {exitingContent}
                      </div>
                    )}

                    {/* Active/entering panel — holds the new children */}
                    <div
                      className={[
                        styles.obPanel,
                        styles.obPanelActive,
                        isAnimating
                          ? direction === "forward"
                            ? styles.animEnterRight
                            : styles.animEnterLeft
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {children}
                    </div>
                  </div>
                )}

                {/* Completion screen — fade in */}
                {showComplete && (
                  <div className={`${styles.obComplete} ${styles.fadeIn}`}>
                    {children}
                  </div>
                )}
              </div>
            </div>
          </>

          <footer className={styles.pageFooter}>
            <span>
              Copyright &copy;{new Date().getFullYear()}. All rights reserved by
              Awasource Ltd
            </span>
            <div className={styles.footerLinks}>
              <Link to="/terms-of-service">Terms of Use</Link>
              <span style={{ color: "#8896ab" }}>|</span>
              <Link to="/privacy-policy"> Privacy Policy</Link>
            </div>
          </footer>
        </div>
      )}
    </>
  );
};

export default OnboardingLayout;
